import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMusicaById, getDatiAzienda } from '../services/api';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import '../print.css';

const MusicaPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [musica, setMusica] = useState(null);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMusicaAndAziendaData = async () => {
      try {
        setLoading(true);
        const [musicaResponse, aziendaResponse] = await Promise.all([
          getMusicaById(id),
          getDatiAzienda()
        ]);
        setMusica(musicaResponse.data);
        setDatiAzienda(aziendaResponse.data);
      } catch (err) {
        setError('Errore nel caricamento dei dati per la stampa.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMusicaAndAziendaData();
  }, [id]);

  useEffect(() => {
    const handleAfterPrint = () => {
      // Introduce a small delay to ensure the print dialog is fully dismissed
      setTimeout(() => {
        navigate('/musica');
      }, 100);
    };

    if (!loading && !error && musica && datiAzienda) {
      window.addEventListener('afterprint', handleAfterPrint);
      window.print();
    }

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [loading, error, musica, datiAzienda, navigate]);

    const renderPrintField = (label, value) => (
      <div className="print-field">
        <span className="label">{label}:</span>
        <span className="value">{value || 'N/D'}</span>
      </div>
    );
  
    if (loading) return <Container className="text-center mt-5"><Spinner animation="border" /><p>Preparazione stampa musica...</p></Container>;
    if (error) return <Container className="mt-5"><Alert variant="danger">{error}</Alert></Container>;
    if (!musica || !datiAzienda) return <Container className="mt-5"><Alert variant="warning">Dati della musica o dell'azienda non trovati.</Alert></Container>;
  
      return (
        <div id="printable-area" className="portrait-page">
          <div className="print-container">          <header className="print-header">
            <div className="company-details">
              {datiAzienda.logo_url && (
                <img
                  src={`${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}`}
                  alt="Logo Azienda"
                  style={{ maxHeight: '150px', maxWidth: '150px', marginRight: '20px', objectFit: 'contain' }}
                />
              )}
            </div>
            <div className="print-info">
              <p><strong>Data di Stampa:</strong> {new Date().toLocaleDateString()}</p>
              <p><strong>ID Musica:</strong> {musica.id}</p>
              <p><strong>SKU:</strong> {musica.sku}</p>
            </div>
          </header>
  
          <main>
            <h2 className="document-title text-center">{musica.titolo}</h2>
  
            {/* ANTEPRIMA IMMAGINE */}
            {musica.immagine_url && (
              <div className="text-center mb-4">
                <img
                  src={musica.immagine_url.startsWith('http') ? musica.immagine_url : process.env.REACT_APP_STATIC_FILES_BASE_URL + musica.immagine_url}
                  alt={"Copertina di " + musica.titolo}
                  style={{ maxHeight: '240px', border: '1px solid #dee2e6', borderRadius: '8px' }}
                />
              </div>
            )}
  
            {/* SCHEDA MUSICA */}
            <section className="print-section">
              <div className="print-section-title">SCHEDA MUSICA</div>
              <div className="print-section-content">
                {renderPrintField('Titolo', musica.titolo)}
                {renderPrintField('Artista', musica.artista)}
                {renderPrintField('Anno', musica.anno)}
                {renderPrintField('Genere', musica.genere)}
                {renderPrintField('Casa di Produzione', musica.casa_di_produzione)}
                <hr />
                <p className="label">Descrizione:</p>
                <p className="value">{musica.descrizione || 'N/D'}</p>
              </div>
            </section>
  
            {/* DETTAGLI PRODOTTO */}
            <section className="print-section">
              <div className="print-section-title">DETTAGLI PRODOTTO</div>
              <div className="print-section-content">
                <Row>
                  <Col xs={6}>{renderPrintField('Categoria', musica.categoria)}</Col>
                  <Col xs={6}>{renderPrintField('Supporto', musica.supporto)}</Col>
                </Row>
                <Row>
                  <Col xs={4}>{renderPrintField('SKU', musica.sku)}</Col>
                  <Col xs={4}>{renderPrintField('Barcode', musica.barcode)}</Col>
                  <Col xs={4}>{renderPrintField('Barcode IBS', musica.barcode_ibs)}</Col>
                </Row>
                {renderPrintField('Ubicazione', musica.ubicazione)}
              </div>
            </section>
  
            {/* PREZZI E MAGAZZINO */}
            <section className="print-section">
              <div className="print-section-title">PREZZI E MAGAZZINO</div>
              <div className="print-section-content">
                <Row>
                  <Col xs={6}>{renderPrintField('Prezzo Negozio', musica.prezzo_negozio ? `€ ${musica.prezzo_negozio.toFixed(2)}` : 'N/D')}</Col>
                  <Col xs={6}>{renderPrintField('Prezzo IBS', musica.prezzo_ibs ? `€ ${musica.prezzo_ibs.toFixed(2)}` : 'N/D')}</Col>
                </Row>
                <Row>
                  <Col xs={6}>{renderPrintField('Costo Acquisto', musica.costo_acquisto ? `€ ${musica.costo_acquisto.toFixed(2)}` : 'N/D')}</Col>
                  <Col xs={6}>{renderPrintField('IVA', musica.iva ? `${musica.iva}%` : 'N/D')}</Col>
                </Row>
                <Row>
                  <Col xs={6}>{renderPrintField('Condizione', musica.condizione)}</Col>
                  <Col xs={6}>{renderPrintField('Quantità', musica.quantita)}</Col>
                </Row>
                {renderPrintField('Fornitore', musica.fornitore?.denominazione)}
              </div>
            </section>
  
            {/* CATALOGO */}
            <section className="print-section">
              <div className="print-section-title">CATALOGO</div>
              <div className="print-section-content">
                {renderPrintField('Fuori Catalogo', musica.fuori_catalogo ? 'Sì' : 'No')}
              </div>
            </section>
          </main>
  
          <footer className="print-footer">
            <p>{datiAzienda.denominazione} - {datiAzienda.indirizzo} - {datiAzienda.cap} {datiAzienda.citta} ({datiAzienda.provincia}) - P.IVA {datiAzienda.piva}</p>
            <p>Email: {datiAzienda.email} - Tel: {datiAzienda.tel}</p>
          </footer>
        </div>
      </div>
    );
  };
export default MusicaPrint;
